import { useTheme } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import CopyText from 'components/Utils/CopyText';
import { HoverEditIcon } from 'components/Utils/IconsAndImages/HoverEditIcon';
import { globalTextStylesDisabled } from 'globalStyles';
import parse, { Element, HTMLReactParserOptions } from 'html-react-parser';
import { ReactNode, useContext, useState } from 'react';
import { clearHtmlValue, sanitizeHtml } from 'utils/html';
import { isEmpty } from 'utils/string';
import {
  createBadgeJSX,
  createSpecialLinkJSX,
  getBadgeTextFromElement,
  getSpecialLinkElement,
  getSpecialLinkHrefFromElement,
  getSpecialLinkTextFromElement,
  isBadgeElement,
} from './RichTextBadgeHelper';
import { IRichTextEditorSpecialLinkType } from './RichTextEditor';

export interface IRichTextReadOnlyProps {
  className?: string;
  html?: string;
  placeholder?: string;
  height?: string | number;
  maxHeight?: string | number; // -1 for unlimited
  onClick?: () => void;
  children?: ReactNode;
  onBadgeClick?: (badgeText: string) => void;
  onSpecialLinkClick?: (type: IRichTextEditorSpecialLinkType, text: string, href: string) => void;
  hideBorder?: boolean;
  hideEditIcon?: boolean;
  stripParagraph?: boolean;
  linkify?: boolean;
  showCopy?: boolean;
  transparent?: boolean;
}

const RichTextReadOnly = (props: IRichTextReadOnlyProps) => {
  const [hover, setHover] = useState<boolean>(false);
  const appContext = useContext(AppContext);
  const theme = useTheme();

  const onBadgeClick = (badgeText: string) => {
    if (props.onBadgeClick) {
      props.onBadgeClick(badgeText);
    }
  };

  const onSpecialLinkClick = (type: IRichTextEditorSpecialLinkType, text: string, href: string) => {
    if (props.onSpecialLinkClick) {
      props.onSpecialLinkClick(type, text, href);
    }
  };

  const onClick = () => {
    if (props.onClick) {
      setHover(false);
      props.onClick();
    }
  };

  //Add a specific onClick event to all badges
  const options: HTMLReactParserOptions = {
    replace: (domNode) => {
      const node = domNode as Element;
      if (isBadgeElement(node)) {
        const badgeText = getBadgeTextFromElement(node);
        if (badgeText) {
          const newBadge = createBadgeJSX(badgeText.trim(), onBadgeClick);

          return newBadge;
        }
      }
      const specialLinkType = getSpecialLinkElement(node);
      if (specialLinkType !== IRichTextEditorSpecialLinkType.None) {
        const text = getSpecialLinkTextFromElement(node);
        const href = getSpecialLinkHrefFromElement(node);
        if (text && href) {
          const specialLink = createSpecialLinkJSX(specialLinkType, text, href, onSpecialLinkClick);

          return specialLink;
        }
      }
    },
  };

  const getValue = () => {
    if (isEmpty(props.html)) {
      return <IPText styles={globalTextStylesDisabled}>{props.placeholder}</IPText>;
    } else {
      const newHtml = clearHtmlValue(props.html ?? '', props.stripParagraph ?? false, true, props.linkify ?? false);
      const clean = sanitizeHtml(newHtml ?? '');

      return parse(clean, options);
    }
  };

  //
  // Main render
  //
  return (
    <IPStack
      className={props.className}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          onClick();
        }
      }}
      onClick={() => {
        onClick();
      }}
      onMouseOver={() => {
        setHover(true);
      }}
      onMouseLeave={() => setHover(false)}
      styles={{
        root: {
          minHeight: 32,
          border: props.hideBorder ? undefined : '1px solid grey',
          borderRadius: props.hideBorder ? undefined : '3px',
          paddingTop: '5px',
          paddingLeft: '10px',
          paddingRight: '10px',
          paddingBottom: '5px',
          color: props.html ? undefined : '#808080',
          background:
            props.transparent || props.onClick
              ? undefined
              : appContext.useDarkMode
              ? theme.palette.themeDarker
              : theme.palette.themeLighterAlt,
          '&:hover': props.onClick
            ? {
                cursor: 'text',
                'border-color': theme.palette.themeSecondary,
                background: theme.palette.neutralLighter,
                transition: 'all 0.3s ease',
              }
            : undefined,
        },
      }}
    >
      <IPStack horizontal horizontalAlign="space-between">
        <IPStack.Item
          grow
          styles={{
            root: {
              height: props.height,
              maxHeight: props.maxHeight === -1 ? undefined : props.maxHeight || 300,
              overflowY: !hover ? 'hidden' : 'scroll',
              overflowX: 'hidden',
            },
          }}
        >
          <div style={{ borderWidth: '5px' }}>{getValue()}</div>
        </IPStack.Item>
        {!props.hideEditIcon && (
          <IPStack.Item>{hover && props.onClick && <HoverEditIcon left={+10} top={-5} />}</IPStack.Item>
        )}
        {props.html && props.showCopy && !props.onClick && (
          <IPStack.Item>
            <CopyText html={props.html} left={+10} top={-5} />
          </IPStack.Item>
        )}
      </IPStack>
      <IPStack.Item>{props.children}</IPStack.Item>
    </IPStack>
  );
};

export default RichTextReadOnly;
